import { toast } from "../../../components/ui/use-toast"
import { getDiscountFieldsForType } from './constants'
import { Promotion } from './types'
import { expected_lift_validation_error, participation_validation_error } from "./planning_assumptions"
import { promotion_type_layout } from "./promotion_type_layout"

/**
 * PURE: the first validation problem with a promotion, or null when it is valid.
 *
 * Kept separate from `validatePromotion` (which toasts) so the rules are unit testable.
 */
export function promotion_validation_error(promotion: Promotion): string | null {
  if (!promotion.name.trim()) {
    return "Promotion name is required"
  }

  if (!promotion.promotion_type?.trim()) {
    return "Promotion type is required"
  }

  const layout = promotion_type_layout(promotion.promotion_type)

  // A flat fee or an accrual is often at the retailer level, not on specific items.
  if (promotion.products.length === 0 && !layout.products_optional) {
    return "At least one product is required"
  }

  if (!promotion.customer?.trim()) {
    return "Customer (distributor) is required"
  }

  // An end date before the start date is a typo, never a deal.
  if (promotion.start_date && promotion.end_date && promotion.end_date < promotion.start_date) {
    return "The end date is before the start date"
  }

  // Submitting is asking someone to approve: there has to be a someone.
  if (promotion.stage === "Submitted" && !promotion.approver_id) {
    return "Pick an approver to submit this promotion for approval"
  }

  // Plan-time assumptions are optional, but a nonsense value would silently distort the estimate.
  const assumption_error = participation_validation_error(promotion.participation_pct) || expected_lift_validation_error(promotion.expected_lift_pct)
  if (assumption_error) return assumption_error

  if (promotion.regular_price != null && promotion.regular_price < 0) {
    return "The regular price can't be negative"
  }

  // Type-specific validation
  if (layout.deal_fields === "multibuy") {
    if (!promotion.multibuy_quantity || !promotion.multibuy_price) {
      return "Both quantity and price are required for multi-buy promotions"
    }
    // Ensure no discount fields are set for multibuy
    if (promotion.discount_amount || promotion.discount_percentage) {
      return "Multi-buy promotions should not have discount amount or percentage"
    }
    return null
  }

  // Ensure no multibuy fields are set for non-multibuy promotions
  if (promotion.multibuy_quantity || promotion.multibuy_price) {
    return "Non-multibuy promotions should not have multibuy quantity or price"
  }

  if (layout.deal_fields === "bogo") {
    // The terms are optional (an imported BOGO may carry only a planned amount, and moving its
    // dates must still save), but half a pair or a zero is a slip.
    const buy = promotion.bogo_buy_quantity
    const get = promotion.bogo_get_quantity
    if ((buy != null) !== (get != null)) {
      return "Enter both the buy and the get quantity for a BOGO (e.g. buy 1, get 1)"
    }
    if ((buy != null && buy < 1) || (get != null && get < 1)) {
      return "BOGO quantities must be at least 1"
    }
    return null
  }

  // A per-unit discount is only required for types that actually offer one. Lump-sum deals — an
  // off-shelf display fee, an ad/marketing program, slotting — carry their whole cost in Planned
  // Amount and have no per-unit rate at all; demanding one here made them unsavable, so an edit as
  // innocent as moving the dates was rejected.
  const discount_fields = getDiscountFieldsForType(promotion.promotion_type || "")
  const shows_discount = discount_fields.showAmount || discount_fields.showPercentage
  const has_lump_sum = (promotion.planned_amount ?? 0) !== 0
  const has_line_terms = (promotion.lines ?? []).some((line) => line.discount_amount || line.discount_percentage)
  if (shows_discount && !promotion.discount_amount && !promotion.discount_percentage && !has_lump_sum && !has_line_terms) {
    return "Enter a discount amount, a discount percentage, or a planned amount for this promotion"
  }

  return null
}

export function validatePromotion(promotion: Promotion): boolean {
  const error = promotion_validation_error(promotion)
  if (error) {
    toast({
      title: "Validation Error",
      description: error,
      variant: "destructive",
    })
    return false
  }

  return true
}

export function validateCalendar(calendarData: {
  name: string
  description?: string
  year?: number
  retailer_name?: string
  brand_name?: string
  customer_name?: string
}): boolean {
  if (!calendarData.name.trim()) {
    toast({
      title: "Validation Error",
      description: "Calendar name is required",
      variant: "destructive",
    })
    return false
  }

  return true
}
